import { Loading } from '@pandiafi/design-system'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { InvitationVerifyCodeForm } from './InvitationVerifyCodeForm/InvitationVerifyCodeForm'
import { ResidentUserRegistrationStatus } from '../../../common/types'

import routePath from 'router/routePath'
import { Error } from 'components'
import { PublicFormResponse } from 'components/PublicFormResponse'
import { CreatePasswordForm } from 'views/Password/CreatePasswordForm'
import { FormErrorSection } from 'views/Login/FormErrorSection'
import { useAPIGetInvitation } from 'api/apiHooks/useAPIGetInvitation'
import { useAPIRegisterInvitedUser } from 'api/apiHooks/useAPIRegisterInvitedUser'

enum InvitationPhase {
  CREATE_PASSWORD = 'create-password',
  VERIFY_CODE = 'verify-code',
  SUCCESS = 'success',
}

export const InvitationRegistration: React.FC = () => {
  const { t } = useTranslation(['registration', 'login'])
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()

  const [advancedPhase, setAdvancedPhase] = useState<InvitationPhase | null>(
    null
  )

  const {
    data: invitation,
    isLoading,
    isError,
  } = useAPIGetInvitation(uuid ?? '')

  const {
    mutate: registerInvitedUser,
    isPending: isRegistering,
    isError: isRegisterError,
  } = useAPIRegisterInvitedUser(uuid ?? '', {
    onSuccess: () => setAdvancedPhase(InvitationPhase.VERIFY_CODE),
  })

  const goToLogin = () => navigate(routePath.login)

  if (!uuid) {
    return <Error message={t('registration:invitationLoadError')} />
  }

  if (isLoading) {
    return <Loading variant="details" />
  }

  if (isError || !invitation || !invitation.email) {
    return <Error message={t('registration:invitationLoadError')} />
  }

  if (invitation.expired) {
    return (
      <PublicFormResponse
        title={t('registration:invitationExpiredTitle')}
        description={t('registration:invitationExpiredDescription')}
        status="error"
        onClick={goToLogin}
        buttonText={t('login:submit')}
      />
    )
  }

  if (
    invitation.registrationStatus === ResidentUserRegistrationStatus.COMPLETE
  ) {
    return (
      <PublicFormResponse
        title={t('registration:invitationAlreadyRegisteredTitle')}
        description={t('registration:invitationAlreadyRegisteredDescription')}
        status="success"
        onClick={goToLogin}
        buttonText={t('login:submit')}
      />
    )
  }

  const phase =
    advancedPhase ??
    (invitation.registrationStatus ===
    ResidentUserRegistrationStatus.PENDING_EMAIL_VERIFICATION
      ? InvitationPhase.VERIFY_CODE
      : InvitationPhase.CREATE_PASSWORD)

  if (phase === InvitationPhase.SUCCESS) {
    return (
      <PublicFormResponse
        title={t('registration:registrationSucceeded')}
        status="success"
        onClick={goToLogin}
        buttonText={t('login:submit')}
      />
    )
  }

  if (phase === InvitationPhase.VERIFY_CODE) {
    return (
      <InvitationVerifyCodeForm
        uuid={uuid}
        email={invitation.email}
        onSuccess={() => setAdvancedPhase(InvitationPhase.SUCCESS)}
      />
    )
  }

  return (
    <div className="flex flex-col gap-5">
      {isRegisterError && (
        <FormErrorSection error={t('registration:invitationRegisterError')} />
      )}
      <CreatePasswordForm
        customHeader={t('registration:invitationCreatePasswordHeader')}
        customDescription={t(
          'registration:invitationCreatePasswordDescription',
          {
            email: invitation.email,
          }
        )}
        showCancel={false}
        onCancel={goToLogin}
        onSuccess={(password) => {
          if (!isRegistering) {
            registerInvitedUser({ password })
          }
        }}
      />
    </div>
  )
}
